import React from 'react';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import {
  Text,
  BlueButtonBasic,
  BlueButtonLink,
  WhiteButtonLink,
  devices,
  Content,
  Stack,
  Spacing,
} from './common';
import { RichTextContent } from './StyledComponents';
import { StrongUnderline, ExternalLink, PhoneIcon, EmailIcon } from './SVG';
import theme from '../theme';
import { JobsPage, Service } from '../@types/types';
import PersonCard from './PersonCard';

type Props = {
  title: string;
  contentData?: Service['DescriptionDetails'][];
  content?: string;
  date?: string;
  signUp?: string;
  employee?: JobsPage['ContactCard'];
  backgroundColor?: string;
};

const Article = ({
  title,
  contentData,
  content,
  date,
  signUp,
  employee,
  backgroundColor,
}: Props) => {
  const formatted = (data: string) => {
    const date = new Date(data);
    return `${format(date, 'dd')}. 
      ${monthTranslation(format(date, 'MM'))} 
      ${format(date, 'yyyy')}`;
  };

  const monthTranslation = (month: string) => {
    switch (month) {
      case '01':
        return 'tammikuuta';
        break;
      case '02':
        return 'helmikuuta';
        break;
      case '03':
        return 'maaliskuuta';
        break;
      case '04':
        return 'huhtikuuta';
        break;
      case '05':
        return 'toukokuuta';
        break;
      case '06':
        return 'kesäkuuta';
        break;
      case '07':
        return 'heinäkuuta';
        break;
      case '08':
        return 'elokuuta';
        break;
      case '09':
        return 'syyskuuta';
        break;
      case '10':
        return 'lokakuuta';
        break;
      case '11':
        return 'marraskuuta';
        break;
      case '12':
        return 'joulukuuta';
        break;
      default:
        return 'tammikuuta';
    }
  };

  return (
    <Background color={backgroundColor || theme.colors.lightBlue}>
      <Container>
        <Stack axis="y" align="center" spacing="small">
          <Title variant="title-2" color="black" align="center">
            {title}
          </Title>
          <StrongUnderline width={320} />
          {date ? (
            <DateText variant="body-s-semi-bold">
              Julkaistu {formatted(date)}
            </DateText>
          ) : null}
          {content ? (
            <RichTextContent>{content}</RichTextContent>
          ) : (
            contentData &&
            contentData?.map(
              (
                {
                  Description,
                  contactRequest,
                  phoneButtonText,
                  phoneNumber,
                  link,
                  linkText,
                },
                index: number,
                array: any[]
              ) => {
                const isLastItem = index === array.length - 1;
                return (
                  <ArticleContainer
                    key={index}
                    axis="y"
                    align="center"
                    spacing="small"
                    style={
                      !isLastItem
                        ? {
                            borderBottom: `1px solid ${theme.colors.darkBlue}`,
                          }
                        : {}
                    }
                  >
                    <RichTextContent>{Description}</RichTextContent>
                    {(contactRequest || phoneNumber || link) && (
                      <>
                        <Spacing axis="y" amount="large" />
                        <ButtonStack axis="x" spacing="default" align="center">
                          {contactRequest && (
                            <CustomBlueButtonLink to="/yhteys#contactForm">
                              <EmailIcon fill={theme.colors.white} /> &nbsp;
                              Jätä yhteydenottopyyntö
                            </CustomBlueButtonLink>
                          )}
                          {phoneNumber && (
                            <WhiteButtonLink href={`tel:${phoneNumber}`}>
                              <PhoneIcon fill={theme.colors.darkBlue} />
                              &nbsp;
                              {phoneButtonText}
                            </WhiteButtonLink>
                          )}
                          {link && (
                            <CustomBlueButtonExternal
                              href={link}
                              target="_blank"
                            >
                              {linkText}
                              <ExternalLinkIcon
                                fill={`${theme.colors.white}`}
                              />
                            </CustomBlueButtonExternal>
                          )}
                        </ButtonStack>
                      </>
                    )}
                  </ArticleContainer>
                );
              }
            )
          )}

          {signUp && (
            <ButtonContainer>
              <CustomBlueButton href={signUp}>
                Ilmoittautuminen
                <ExternalLinkIcon fill={`${theme.colors.white}`} />
              </CustomBlueButton>
            </ButtonContainer>
          )}
          <Spacing axis="y" amount="xlarge" />
          {employee && <PersonCard contact={employee} />}
        </Stack>
      </Container>
    </Background>
  );
};

export default Article;

const Background = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
`;

const Container = styled(Content)`
  padding: 80px;

  @media ${devices.mobileXL} {
    padding: ${(props) => props.theme.spacing.xxlarge}
      ${(props) => props.theme.spacing.medium};
  }

  @media ${devices.mobileS} {
    padding: ${(props) => props.theme.spacing.xxlarge}
      ${(props) => props.theme.spacing.small};
  }
`;

const DateText = styled(Text)`
  padding: ${(props) => props.theme.spacing.large};
`;

const ExternalLinkIcon = styled(ExternalLink)`
  margin-left: 10px;
`;

const ButtonContainer = styled.div`
  width: 100%;
  max-width: 70ch;
`;

const CustomBlueButton = styled(BlueButtonBasic)`
  width: max-content;
  font-size: ${(props) => props.theme.sizing.default};
  font-weight: bold;
  margin-left: 0;
  margin-top: 20px;

  @media ${devices.tablet} {
    margin-right: 0;
    align-self: center;
  }
`;

const CustomBlueButtonLink = styled(BlueButtonLink)`
  width: max-content;

  @media ${devices.tablet} {
    margin-right: 0;
    align-self: center;
  }

  @media ${devices.mobileXL} {
    width: 100%;
    word-break: break-all;
  }
`;

const CustomBlueButtonExternal = styled(BlueButtonBasic)`
  width: max-content;

  @media ${devices.tablet} {
    margin-right: 0;
    align-self: center;
  }

  @media ${devices.mobileXL} {
    width: 100%;
    word-break: break-all;
  }
`;

const Title = styled(Text)`
  @media ${devices.mobileXL} {
    text-align: center;
    word-break: break-all;
  }
`;

const ButtonStack = styled(Stack)`
  @media ${devices.mobileXL} {
    flex-direction: column;
  }
`;

const ArticleContainer = styled(Stack)`
  padding-bottom: ${(props) => props.theme.spacing.xlarge};
`;
